/**
 * Live picking: everyone on a split sees who has joined each item.
 *
 * Backend contract (WebSocket, JSON text frames):
 *   connect   `${REACT_APP_SPLIT_WS_URL}?split=<splitId>`
 *   client →  ClientMessage   (join first, then pick / unpick / paid)
 *   server →  ServerMessage   (a full `state` snapshot after every change)
 * The server applies messages with the same rules as `reduce` below, never
 * sends anyone's MoMo number to other clients, and sets `closed` when the
 * host closes the split (picks stop changing).
 *
 * Without REACT_APP_SPLIT_WS_URL a local stand-in keeps tabs of the same
 * browser in sync instead, for development.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Split } from './model';

export type Member = { id: string; name: string; host: boolean; paid: boolean };

export type RoomState = {
  splitId: string;
  members: Member[];
  /** Item id to the ids of everyone on it, in the order they joined. */
  picks: Record<string, string[]>;
  closed: boolean;
};

export type ClientMessage =
  | {
      type: 'join';
      splitId: string;
      member: { id: string; name: string; momo: string };
      /** What the link knows, for a server that hasn't seen this split yet. */
      seed: RoomState;
    }
  | { type: 'pick' | 'unpick'; splitId: string; memberId: string; itemId: string }
  | { type: 'paid'; splitId: string; memberId: string; amount: number };

export type ServerMessage = { type: 'state'; state: RoomState } | { type: 'error'; message: string };

export type LiveStatus = 'connecting' | 'live' | 'offline' | 'local';

/** The room as the shared link describes it: its people and the picks made in the app. */
export function seedRoom(split: Split): RoomState {
  const picks: Record<string, string[]> = {};
  for (const item of split.receipt.items) picks[item.id] = [...(split.assignments[item.id] ?? [])];
  return {
    splitId: split.id,
    members: split.participants.map((p) => ({ id: p.id, name: p.name, host: p.isCreator, paid: false })),
    picks,
    closed: false,
  };
}

/** The rules for every change. Unknown items or people and a closed split change nothing. */
export function reduce(state: RoomState, msg: ClientMessage): RoomState {
  if (msg.splitId !== state.splitId) return state;
  if (msg.type === 'join') {
    const { id, name } = msg.member;
    const known = state.members.find((m) => m.id === id);
    const members = known
      ? state.members.map((m) => (m.id === id ? { ...m, name } : m))
      : [...state.members, { id, name, host: false, paid: false }];
    return { ...state, members };
  }
  if (!state.members.some((m) => m.id === msg.memberId)) return state;
  if (msg.type === 'paid') {
    return {
      ...state,
      members: state.members.map((m) => (m.id === msg.memberId ? { ...m, paid: true } : m)),
    };
  }
  const owners = state.picks[msg.itemId];
  if (state.closed || !owners) return state;
  const on = owners.includes(msg.memberId);
  if ((msg.type === 'pick') === on) return state;
  const next = msg.type === 'pick' ? [...owners, msg.memberId] : owners.filter((id) => id !== msg.memberId);
  return { ...state, picks: { ...state.picks, [msg.itemId]: next } };
}

type Handlers = { onState: (s: RoomState) => void; onStatus: (s: LiveStatus) => void };
type Connection = { send: (msg: ClientMessage) => void; close: () => void };

function isRoomState(v: any): v is RoomState {
  return v && typeof v.splitId === 'string' && Array.isArray(v.members) && v.picks && typeof v.picks === 'object';
}

/** Stand-in server: state in localStorage, changes broadcast to the other tabs. */
function connectLocal(splitId: string, { onState, onStatus }: Handlers): Connection {
  const key = `pebla.splitRoom.${splitId}`;
  const channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(key) : null;
  const read = (): RoomState | null => {
    try {
      const s = JSON.parse(localStorage.getItem(key) ?? 'null');
      return isRoomState(s) ? s : null;
    } catch {
      return null;
    }
  };
  if (channel) channel.onmessage = (e) => isRoomState(e.data) && onState(e.data);
  onStatus('local');
  return {
    send(msg) {
      const current = read() ?? (msg.type === 'join' ? msg.seed : null);
      if (!current) return;
      const next = reduce(current, msg);
      try {
        localStorage.setItem(key, JSON.stringify(next));
      } catch {
        /* storage full or blocked; this tab still updates */
      }
      channel?.postMessage(next);
      onState(next);
    },
    close: () => channel?.close(),
  };
}

function connectSocket(base: string, splitId: string, { onState, onStatus }: Handlers): Connection {
  let socket: WebSocket | null = null;
  let closed = false;
  let attempt = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let join: ClientMessage | null = null;
  const queue: ClientMessage[] = [];

  const open = () => {
    onStatus(attempt === 0 ? 'connecting' : 'offline');
    const url = `${base}${base.includes('?') ? '&' : '?'}split=${encodeURIComponent(splitId)}`;
    const ws = new WebSocket(url);
    socket = ws;
    ws.onopen = () => {
      attempt = 0;
      onStatus('live');
      if (join) ws.send(JSON.stringify(join));
      while (queue.length) ws.send(JSON.stringify(queue.shift()));
    };
    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(String(e.data)) as ServerMessage;
        if (msg.type === 'state' && isRoomState(msg.state)) onState(msg.state);
      } catch {
        /* not ours */
      }
    };
    ws.onclose = () => {
      if (closed) return;
      onStatus('offline');
      timer = setTimeout(open, Math.min(1000 * 2 ** attempt++, 15000));
    };
  };
  open();

  return {
    send(msg) {
      if (msg.type === 'join') join = msg;
      if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify(msg));
      else if (msg.type !== 'join') queue.push(msg);
    },
    close() {
      closed = true;
      clearTimeout(timer);
      socket?.close();
    },
  };
}

const socketUrl = process.env.REACT_APP_SPLIT_WS_URL;

/**
 * Joins [split] as [me] and keeps the room in sync. Changes show at once and
 * are corrected by the next snapshot from the server. Pass null to stay out
 * (splits that aren't picked item by item).
 */
export function useSplitRoom(split: Split, me: { id: string; name: string; momo: string } | null) {
  const [state, setState] = useState<RoomState>(() => seedRoom(split));
  const [status, setStatus] = useState<LiveStatus>('connecting');
  const connection = useRef<Connection | null>(null);
  const meId = me?.id;
  const meName = me?.name;
  const meMomo = me?.momo;

  useEffect(() => {
    if (!meId || meName === undefined || meMomo === undefined) return;
    const handlers: Handlers = {
      onState: (s) => s.splitId === split.id && setState(s),
      onStatus: setStatus,
    };
    const c = socketUrl ? connectSocket(socketUrl, split.id, handlers) : connectLocal(split.id, handlers);
    connection.current = c;
    const join: ClientMessage = {
      type: 'join',
      splitId: split.id,
      member: { id: meId, name: meName, momo: meMomo },
      seed: seedRoom(split),
    };
    setState((s) => reduce(s, join));
    c.send(join);
    return () => {
      connection.current = null;
      c.close();
    };
  }, [split, meId, meName, meMomo]);

  const send = useCallback((msg: ClientMessage) => {
    setState((s) => reduce(s, msg));
    connection.current?.send(msg);
  }, []);

  const latest = useRef(state);
  latest.current = state;

  const toggle = useCallback(
    (itemId: string) => {
      if (!meId) return;
      const on = (latest.current.picks[itemId] ?? []).includes(meId);
      send({ type: on ? 'unpick' : 'pick', splitId: split.id, memberId: meId, itemId });
    },
    [meId, send, split.id],
  );

  const markPaid = useCallback(
    (amount: number) => {
      if (meId) send({ type: 'paid', splitId: split.id, memberId: meId, amount });
    },
    [meId, send, split.id],
  );

  return { state, status, toggle, markPaid };
}
